import { useState, useEffect, useRef } from "react";
import { instance } from "utils/axios";

const C = { purple: "#7B2FBE", teal: "#00BCD4", orange: "#FF5722", yellow: "#FFD600", border: "#e8e0f5", bg: "#f7f4fc" };
const SECTION_COLORS = [C.teal, C.orange, C.purple, "#e6a800", "#16a34a", "#0369a1"];

// ─── SHARED ───────────────────────────────────────────────────────────────────
function ProgressBar({ current, total }) {
  const pct = Math.round((current / total) * 100);
  return (
    <div style={{ width: "100%", maxWidth: 660, margin: "0 auto 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#999", marginBottom: 5 }}>
        <span>{current} / {total}</span><span>{pct}%</span>
      </div>
      <div style={{ height: 5, background: "#ede9f7", borderRadius: 99 }}>
        <div style={{ height: "100%", width: `${pct}%`, background: `linear-gradient(90deg,${C.purple},${C.teal})`, borderRadius: 99, transition: "width 0.4s ease" }} />
      </div>
    </div>
  );
}

const inputBase = (err) => ({
  width: "100%", padding: "11px 14px", borderRadius: 10,
  border: `1.5px solid ${err ? "#e53935" : C.border}`,
  fontSize: 15, outline: "none", fontFamily: "inherit",
  boxSizing: "border-box", background: "#fafafa",
});

function Label({ children, required }) {
  return (
    <label style={{ display: "block", fontSize: 14, fontWeight: 700, color: "#333", marginBottom: 7 }}>
      {children}{required && <span style={{ color: "#e53935", marginLeft: 3 }}>*</span>}
    </label>
  );
}
function ErrMsg() { return <div style={{ color: "#e53935", fontSize: 12, marginTop: 5 }}>⚠ Заавал бөглөнө үү</div>; }

// ─── MATRIX ───────────────────────────────────────────────────────────────────
function MatrixField({ q, value = {}, onChange, color, error }) {
  const rows = (q.question_rows || []).map(r => r.row);
  const columns = (q.question_answers || []).map(a => a.answer);
  const filled = rows.filter(r => value[r]).length;

  return (
    <div>
      <div style={{ fontSize: 12, color: "#999", marginBottom: 8 }}>
        {filled} / {rows.length} бөглөгдлөө
        <div style={{ height: 3, background: "#ede9f7", borderRadius: 99, marginTop: 3 }}>
          <div style={{ height: "100%", width: `${Math.round((filled / rows.length) * 100)}%`, background: color, borderRadius: 99, transition: "width 0.2s" }} />
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
          <thead>
            <tr>
              <th style={{ width: "35%", padding: "10px 14px", borderBottom: `2px solid ${C.border}` }} />
              {q.minLabel && (
                <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 400, color: "#888", fontSize: 12, borderBottom: `2px solid ${C.border}`, whiteSpace: "nowrap" }}>
                  {q.minLabel}
                </th>
              )}
              {columns.map((col, i) => (
                <th key={i} style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, color: "#333", borderBottom: `2px solid ${C.border}`, lineHeight: 1.4, verticalAlign: "bottom", minWidth: 50 }}>
                  {col}
                </th>
              ))}
              {q.maxLabel && (
                <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 400, color: "#888", fontSize: 12, borderBottom: `2px solid ${C.border}`, whiteSpace: "nowrap" }}>
                  {q.maxLabel}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} style={{ background: ri % 2 === 0 ? "#fafafa" : "#fff" }}>
                <td style={{ padding: "12px 14px", fontWeight: 500, color: "#222", borderBottom: `1px solid ${C.border}`, lineHeight: 1.4 }}>{row}</td>
                {columns.map((col, ci) => (
                  <td key={ci} style={{ padding: "12px 8px", textAlign: "center", borderBottom: `1px solid ${C.border}` }}>
                    <input type="radio" name={`matrix_${q.id}_${ri}`} checked={value[row] === col}
                      onChange={() => onChange({ ...value, [row]: col })}
                      style={{ accentColor: color, width: 18, height: 18, cursor: "pointer" }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {error && <div style={{ color: "#e53935", fontSize: 12, marginTop: 8 }}>⚠ Бүх мөрийг бөглөнө үү ({rows.length - filled} үлдсэн)</div>}
    </div>
  );
}

// ─── FILE UPLOAD ──────────────────────────────────────────────────────────────
function FileUpload({ value, onChange, onBlur, error, q, applicantAdmissionId }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const ref = useRef();

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 300 * 1024 * 1024) {
      alert("Файлын хэмжээ 300МБ-аас хэтрэхгүй байх ёстой");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (applicantAdmissionId) fd.append("applicantAdmissionId", applicantAdmissionId);
      fd.append("questionId", q.id);
      const res = await instance.post("applicant2/file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded / e.total) * 100)),
      });
      // onChange → handleChange → changedQRef тохируулна
      onChange(res.data.fileId);
      // onBlur → autosave → DB-д хадгална
      if (onBlur) onBlur();
    } catch {
      alert("Файл хуулахад алдаа гарлаа. Дахин оролдоно уу.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div
        onClick={() => !uploading && ref.current.click()}
        style={{ borderRadius: 12, border: `2px dashed ${error ? "#e53935" : value ? "#7B2FBE" : "#e8e0f5"}`, background: value ? "#f3eaff" : "#fafafa", padding: 20, textAlign: "center", cursor: uploading ? "not-allowed" : "pointer", transition: "all 0.2s" }}
      >
        {uploading ? (
          <div>
            <div style={{ fontSize: 13, color: "#7B2FBE", fontWeight: 700, marginBottom: 8 }}>⏳ Хуулж байна... {progress}%</div>
            <div style={{ height: 6, background: "#ede9f7", borderRadius: 99 }}>
              <div style={{ height: "100%", width: `${progress}%`, background: "#7B2FBE", borderRadius: 99, transition: "width 0.3s" }} />
            </div>
          </div>
        ) : value ? (
          <div style={{ fontSize: 13, color: "#7B2FBE", fontWeight: 700 }}>
            ✅ Файл хуулагдлаа<br />
            <a href={`https://drive.google.com/file/d/${value}/view`} target="_blank" rel="noreferrer"
              style={{ fontSize: 12, color: "#0369a1", fontWeight: 400 }} onClick={e => e.stopPropagation()}>
              Google Drive-д үзэх →
            </a><br />
            <span style={{ fontSize: 11, color: "#aaa", fontWeight: 400 }}>Өөрчлөхийн тулд дарна уу</span>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 26, marginBottom: 4 }}>📎</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#666" }}>Файл хавсаргах</div>
            <div style={{ fontSize: 11, color: "#bbb", marginTop: 2 }}>
              {q.allowedTypes || "Видео файл"} · Дээд тал {q.maxFileSize || 300}МБ
            </div>
          </div>
        )}
      </div>
      <input ref={ref} type="file" accept={q.allowedTypes || "video/*"} style={{ display: "none" }} onChange={handleFile} />
      {error && <div style={{ color: "#e53935", fontSize: 12, marginTop: 5 }}>⚠ Файл хавсаргана уу</div>}
    </div>
  );
}

// ─── QUESTION FIELD ───────────────────────────────────────────────────────────
function QuestionField({ q, value, onChange, error, color, onBlur, applicantAdmissionId }) {
  const base = inputBase(error);
  const options = q.question_answers || [];

  if (q.qType === "matrix_radio" || q.qType === "matrix_check") {
    return <MatrixField q={q} value={value || {}} onChange={(val) => { onChange(val); onBlur && onBlur(); }} color={color} error={error} />;
  }
  if (q.qType === "radio") return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {options.map(opt => (
        <label key={opt.id} onClick={() => { onChange(opt.answer); onBlur && onBlur(); }}
          style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "8px 14px", borderRadius: 50, border: `1.5px solid ${value === opt.answer ? color : C.border}`, background: value === opt.answer ? `${color}15` : "#fff", fontSize: 13, fontWeight: value === opt.answer ? 700 : 400, transition: "all 0.15s", whiteSpace: "nowrap" }}>
          <input type="radio" name={`q_${q.id}`} checked={value === opt.answer} onChange={() => { }} style={{ accentColor: color, width: 14, height: 14 }} />
          {opt.answer}
        </label>
      ))}
    </div>
  );
  if (q.qType === "checkbox") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {options.map(opt => {
        const checked = Array.isArray(value) && value.includes(opt.answer);
        return (
          <label key={opt.id} onClick={() => {
            const arr = Array.isArray(value) ? [...value] : [];
            const next = checked ? arr.filter(v => v !== opt.answer) : [...arr, opt.answer];
            onChange(next); onBlur && onBlur();
          }} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "9px 14px", borderRadius: 10, border: `1.5px solid ${checked ? color : C.border}`, background: checked ? `${color}12` : "#fff", fontSize: 14, fontWeight: checked ? 700 : 400, transition: "all 0.15s" }}>
            <input type="checkbox" checked={checked} onChange={() => { }} style={{ accentColor: color, width: 16, height: 16 }} />
            {opt.answer}
          </label>
        );
      })}
    </div>
  );
  if (q.qType === "scale") {
    const min = q.minValue || 1;
    const max = q.maxValue || 5;
    const steps = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {q.minLabel && <span style={{ fontSize: 12, color: "#888", whiteSpace: "nowrap" }}>{q.minLabel}</span>}
          <div style={{ display: "flex", gap: 6, flexWrap: "nowrap" }}>
            {steps.map(s => (
              <label key={s} onClick={() => { onChange(String(s)); onBlur && onBlur(); }}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: "pointer" }}>
                <div style={{ width: 36, height: 36, borderRadius: 50, border: `2px solid ${value === String(s) ? color : C.border}`, background: value === String(s) ? color : "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: value === String(s) ? "#fff" : "#555", transition: "all 0.15s" }}>
                  {s}
                </div>
              </label>
            ))}
          </div>
          {q.maxLabel && <span style={{ fontSize: 12, color: "#888", whiteSpace: "nowrap" }}>{q.maxLabel}</span>}
        </div>
      </div>
    );
  }
  if (q.qType === "select") return (
    <select value={value || ""} onChange={e => onChange(e.target.value)} onBlur={() => onBlur && onBlur()} style={base}>
      <option value="">Сонгоно уу...</option>
      {options.map(opt => <option key={opt.id} value={opt.answer}>{opt.answer}</option>)}
    </select>
  );
  if (q.qType === "textarea") return (
    <textarea value={value || ""} onChange={e => onChange(e.target.value)} onBlur={() => onBlur && onBlur()}
      placeholder={q.note || ""} rows={4} style={{ ...base, resize: "vertical", lineHeight: 1.6 }} />
  );
  if (q.qType === "file") return (
    <FileUpload
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      error={error}
      q={q}
      applicantAdmissionId={applicantAdmissionId}
    />
  );
  return (
    <input type="text" value={value || ""}
      onChange={e => onChange(e.target.value)} onBlur={() => onBlur && onBlur()}
      placeholder={q.note || ""} style={base} />
  );
}

// ─── QUESTION CARD ────────────────────────────────────────────────────────────
function QuestionCard({ q, value, onChange, onBlur, error, index, color, applicantAdmissionId }) {
  const isMatrix = q.qType === "matrix_radio" || q.qType === "matrix_check";
  return (
    <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 14px rgba(0,0,0,0.06)", padding: isMatrix ? "22px 18px" : "22px 26px", marginBottom: 12, borderLeft: `4px solid ${color}`, width: "100%", maxWidth: isMatrix ? 780 : 660, boxSizing: "border-box" }}>
      <Label required={q.required}>{index + 1}. {q.question}</Label>
      {q.note && !["textarea"].includes(q.qType) && (
        <div style={{ fontSize: 13, color: "#888", marginBottom: 10 }}>{q.note}</div>
      )}
      <QuestionField
        q={q}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        error={error}
        color={color}
        applicantAdmissionId={applicantAdmissionId}
      />
      {error && !isMatrix && <ErrMsg />}
    </div>
  );
}

// ─── SECTION VIEW ─────────────────────────────────────────────────────────────
function SectionView({ section, index, total, answers, onChange, onBlur, onNext, onBack, errors, submitting, applicantAdmissionId }) {
  const color = SECTION_COLORS[index % SECTION_COLORS.length];
  const isLast = index === total - 1;

  const isVisible = (q) => {
    if (!q.dependsOnId) return true;
    const key = q.dependsOnId;
    const parentAnswer = answers[key] ?? answers[String(key)] ?? answers[Number(key)];
    if (parentAnswer === undefined || parentAnswer === null || parentAnswer === "") return false;
    return parseInt(parentAnswer) >= parseInt(q.dependsOnValue);
  };

  const visibleQuestions = section.questions.filter(isVisible);
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 16px 60px" }}>
      <ProgressBar current={index + 1} total={total} />
      <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 10px rgba(0,0,0,0.06)", padding: "14px 20px", marginBottom: 12, borderTop: `3px solid ${color}`, width: "100%", maxWidth: 660, boxSizing: "border-box" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 3 }}>{index + 1} / {total}</div>
        <h2 style={{ fontSize: 15, fontWeight: 800, color: "#1a1a2e", margin: 0 }}>{section.title}</h2>
        {section.description && <p style={{ color: "#999", fontSize: 12, margin: "5px 0 0", lineHeight: 1.5 }}>{section.description}</p>}
      </div>
      {visibleQuestions.map((q, i) => (
        <QuestionCard
          key={q.id}
          q={q}
          value={answers[q.id]}
          onChange={val => onChange(q.id, val)}
          onBlur={() => onBlur(answers)}
          error={errors[q.id]}
          index={i}
          color={color}
          applicantAdmissionId={applicantAdmissionId}
        />
      ))}
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%", maxWidth: 660, marginTop: 16 }}>
        <button onClick={onBack} style={{ background: "#eee", color: "#444", border: "none", borderRadius: 50, padding: "12px 28px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          ← Буцах
        </button>
        <button onClick={onNext} disabled={submitting}
          style={{ background: isLast ? C.orange : C.purple, color: "#fff", border: "none", borderRadius: 50, padding: "13px 36px", fontSize: 15, fontWeight: 800, cursor: "pointer", opacity: submitting ? 0.7 : 1, fontFamily: "inherit", transition: "transform 0.15s" }}
          onMouseEnter={e => e.currentTarget.style.transform = "scale(1.03)"}
          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
          {submitting ? "Хадгалж байна..." : isLast ? "Дуусгах →" : "Дараах →"}
        </button>
      </div>
    </div>
  );
}

// ─── SKELETON ─────────────────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 16px", gap: 12, maxWidth: 660, margin: "0 auto" }}>
      {[1, 2, 3].map(i => (
        <div key={i} style={{ background: "#ede9f7", borderRadius: 12, height: 80, width: "100%", animation: "pulse 1.5s ease-in-out infinite", animationDelay: `${i * 0.15}s` }} />
      ))}
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function Sections({ stageId, applicantAdmissionId, onNext, onBack }) {
  const [sections, setSections] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!stageId) return;
    instance.get(`applicant2/form/${stageId}`, {
      params: { applicantAdmissionId }
    })
      .then(res => {
        const { sections, existingAnswers } = res.data;
        setSections(sections);

        if (existingAnswers?.length > 0) {
          const built = {};
          existingAnswers.forEach(ua => {
            const qId = ua.questionId;
            if (ua.rowId && ua.question_row) {
              if (!built[qId]) built[qId] = {};
              built[qId][ua.question_row.row] = ua.question_answer?.answer || ua.answer;
            } else if (!ua.answer && ua.question_answer) {
              if (!built[qId]) built[qId] = [];
              built[qId] = [...(built[qId] || []), ua.question_answer.answer];
            } else if (ua.question_answer) {
              built[qId] = ua.question_answer.answer;
            } else if (ua.answer) {
              built[qId] = ua.answer;
            }
          });
          setAnswers(built);
        }

        setLoading(false);
      })
      .catch(() => { setLoadError("Асуулт ачаалахад алдаа гарлаа."); setLoading(false); });
  }, [stageId]);

  const isSavingRef = useRef(false);
  const changedQRef = useRef(null);

  const handleChange = (qId, val) => {
    setAnswers(p => ({ ...p, [qId]: val }));
    changedQRef.current = { qId, val };
    if (errors[qId]) setErrors(p => ({ ...p, [qId]: false }));
  };

  const handleBlur = async () => {
    if (!changedQRef.current) return;
    const { qId, val } = changedQRef.current;
    changedQRef.current = null;

    isSavingRef.current = true;
    try {
      await instance.post(`applicant2/form/${stageId}`, {
        answers: prepareAnswers({ [qId]: val }),
        sectionId: sections[current]?.id,
        isLast: false,
        isAutosave: true,
        applicantAdmissionId,
      });
    } catch { }
    finally {
      isSavingRef.current = false;
    }
  };

  const validate = () => {
    const section = sections[current];
    const errs = {};

    const isVisible = (q) => {
      if (!q.dependsOnId) return true;
      const key = q.dependsOnId;
      const parentAnswer = answers[key] ?? answers[String(key)] ?? answers[Number(key)];
      if (parentAnswer === undefined || parentAnswer === null || parentAnswer === "") return false;
      return parseInt(parentAnswer) >= parseInt(q.dependsOnValue);
    };

    section.questions.filter(isVisible).forEach(q => {
      if (!q.required) return;
      const v = answers[q.id];
      const isMatrix = q.qType === "matrix_radio" || q.qType === "matrix_check";
      if (isMatrix) {
        const rows = (q.question_rows || []).map(r => r.row);
        if (!rows.every(row => v && v[row])) errs[q.id] = true;
      } else if (!v || (Array.isArray(v) && v.length === 0) || (typeof v === "string" && v.trim() === "")) {
        errs[q.id] = true;
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const prepareAnswers = (rawAnswers) => {
    const result = {};
    sections.flatMap(s => s.questions).forEach(q => {
      const v = rawAnswers[q.id];
      if (v === undefined || v === null) return;
      const isMatrix = q.qType === "matrix_radio" || q.qType === "matrix_check";
      if (isMatrix && typeof v === "object" && !Array.isArray(v)) {
        result[q.id] = Object.entries(v).map(([question, answer]) => {
          const qRow = (q.question_rows || []).find(r => r.row === question);
          const qa = (q.question_answers || []).find(a => a.answer === answer);
          return { question, answer, answerId: qa?.id || null, rowId: qRow?.id || null };
        });
      } else {
        result[q.id] = v;
      }
    });
    return result;
  };

  const handleNext = async () => {
    if (!validate()) { window.scrollTo({ top: 0, behavior: "smooth" }); return; }

    const isLast = current === sections.length - 1;
    setSubmitting(true);
    try {
      while (isSavingRef.current) {
        await new Promise(r => setTimeout(r, 50));
      }

      if (isLast) {
        await instance.post(`applicant2/form/${stageId}`, {
          answers: {},
          sectionId: sections[current].id,
          isLast: true,
          applicantAdmissionId,
        });
      }

      if (!isLast) {
        setCurrent(c => c + 1);
        setErrors({});
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        onNext();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (current === 0) { onBack(); return; }
    setCurrent(c => c - 1);
    setErrors({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div style={{ minHeight: "60vh", background: C.bg, fontFamily: "'Raleway','Segoe UI',sans-serif" }}>
      {!loading && sections.length > 0 && (
        <div style={{ background: "#fff", borderBottom: "1px solid #eee", padding: "8px 16px", display: "flex", gap: 6, overflowX: "auto", justifyContent: "center" }}>
          {sections.map((s, i) => {
            const color = SECTION_COLORS[i % SECTION_COLORS.length];
            const isDone = i < current;
            const isActive = i === current;
            const shortTitle = s.title.length > 14 ? s.title.slice(0, 14) + "…" : s.title;
            return (
              <div key={s.id} title={s.title}
                style={{ fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 99, background: isDone ? "#e8f5e9" : isActive ? color : "#eee", color: isDone ? "#388e3c" : isActive ? "#fff" : "#999", flexShrink: 0, transition: "all 0.2s", cursor: "default", maxWidth: 130, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {isDone ? "✓" : i + 1}. {shortTitle}
              </div>
            );
          })}
        </div>
      )}

      {loading && <Skeleton />}
      {loadError && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 60, textAlign: "center" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontSize: 15, color: "#e53935", marginBottom: 20 }}>{loadError}</div>
          <button onClick={() => window.location.reload()} style={{ background: C.purple, color: "#fff", border: "none", borderRadius: 50, padding: "12px 28px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            Дахин оролдох
          </button>
        </div>
      )}

      {!loading && !loadError && sections.length > 0 && (
        <SectionView
          section={sections[current]}
          index={current}
          total={sections.length}
          answers={answers}
          onChange={handleChange}
          onBlur={() => handleBlur()}
          onNext={handleNext}
          onBack={handleBack}
          errors={errors}
          submitting={submitting}
          applicantAdmissionId={applicantAdmissionId}
        />
      )}
    </div>
  );
}